import React, { useState, useEffect } from "react";
import { useNavigate, useSearchParams, Link } from "react-router-dom";
import { Card, CardContent, CardDescription, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { Input } from "@/components/ui/input";
import { Label } from "@/components/ui/label";
import { Alert, AlertDescription } from "@/components/ui/alert";
import { Loader2, CheckCircle, Mail, User, Lock, Building2, ArrowRight, LogIn, UserPlus } from "lucide-react";
import axios from "axios";
import { getApiUrl } from "@/config/appConfig";

export default function AcceptInvitePage() {
  const navigate = useNavigate();
  const [searchParams] = useSearchParams();
  const token = searchParams.get("token");
  
  const [loading, setLoading] = useState(true);
  const [processing, setProcessing] = useState(false);
  const [error, setError] = useState("");
  const [success, setSuccess] = useState(false);
  const [inviteInfo, setInviteInfo] = useState(null);
  const [currentUser, setCurrentUser] = useState(null);
  const [showSignup, setShowSignup] = useState(false);
  const [showLogin, setShowLogin] = useState(false);
  
  // Signup form
  const [signupData, setSignupData] = useState({
    name: "",
    email: "",
    password: "",
    confirmPassword: ""
  });
  
  // Login form
  const [loginData, setLoginData] = useState({
    email: "",
    password: ""
  });
  
  const API = getApiUrl();
  
  // Check token validity and current user status
  useEffect(() => {
    const checkInvite = async () => {
      if (!token) {
        setError("Invalid invitation link");
        setLoading(false);
        return;
      }
      
      try {
        // Validate invite token
        const inviteRes = await axios.get(`${API}/team/invite/validate/${token}`);
        setInviteInfo(inviteRes.data);
        
        // Check if user is already logged in
        try {
          const meRes = await axios.get(`${API}/auth/me`, { withCredentials: true });
          if (meRes.data) {
            setCurrentUser(meRes.data);
          }
        } catch (e) {
          // Not logged in, that's ok
        }
        
        setLoading(false);
      } catch (err) {
        setError(err.response?.data?.detail || "Invalid or expired invitation");
        setLoading(false);
      }
    };
    
    checkInvite();
  }, [token, API]);
  
  // Accept invite as logged-in user
  const handleAcceptAsCurrentUser = async () => {
    setProcessing(true);
    setError("");
    
    try {
      await axios.post(`${API}/team/invite/accept`, { token }, { withCredentials: true });
      setSuccess(true);
      setTimeout(() => navigate("/dashboard"), 2000);
    } catch (err) {
      setError(err.response?.data?.detail || "Failed to accept invitation");
    }
    
    setProcessing(false);
  };
  
  // Login and accept invite
  const handleLoginAndAccept = async (e) => {
    e.preventDefault();
    setProcessing(true);
    setError("");
    
    try {
      // Login
      const loginRes = await axios.post(`${API}/auth/login`, loginData, { withCredentials: true });
      
      if (loginRes.data.success) {
        // Accept invite
        await axios.post(`${API}/team/invite/accept`, { token }, { withCredentials: true });
        setSuccess(true);
        setTimeout(() => navigate("/dashboard"), 2000);
      } else {
        setError("Login failed. Please check your credentials.");
      }
    } catch (err) {
      setError(err.response?.data?.detail || "Login failed");
    }
    
    setProcessing(false);
  };
  
  // Signup and accept invite
  const handleSignupAndAccept = async (e) => {
    e.preventDefault();
    setProcessing(true);
    setError("");
    
    if (signupData.password !== signupData.confirmPassword) {
      setError("Passwords do not match");
      setProcessing(false);
      return;
    }
    
    if (signupData.password.length < 8) {
      setError("Password must be at least 8 characters");
      setProcessing(false);
      return;
    }
    
    try {
      // Signup with invite token
      await axios.post(`${API}/auth/signup`, {
        name: signupData.name,
        email: signupData.email || inviteInfo?.email,
        password: signupData.password,
        invite_token: token
      }, { withCredentials: true });
      
      setSuccess(true);
      setTimeout(() => navigate("/dashboard"), 2000);
    } catch (err) {
      setError(err.response?.data?.detail || "Signup failed");
    }
    
    setProcessing(false);
  };
  
  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-violet-50 to-indigo-100">
        <Card className="w-full max-w-md">
          <CardContent className="p-8 text-center">
            <Loader2 className="w-8 h-8 animate-spin mx-auto text-violet-600" />
            <p className="mt-4 text-gray-600">Validating invitation...</p>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  if (error && !inviteInfo) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-violet-50 to-indigo-100">
        <Card className="w-full max-w-md">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 bg-red-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <Mail className="w-8 h-8 text-red-600" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Invalid Invitation</h2>
            <p className="text-gray-600 mb-6">{error}</p>
            <Button asChild>
              <Link to="/">Go to Homepage</Link>
            </Button>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  if (success) {
    return (
      <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-violet-50 to-indigo-100">
        <Card className="w-full max-w-md">
          <CardContent className="p-8 text-center">
            <div className="w-16 h-16 bg-green-100 rounded-full flex items-center justify-center mx-auto mb-4">
              <CheckCircle className="w-8 h-8 text-green-600" />
            </div>
            <h2 className="text-xl font-semibold text-gray-900 mb-2">Welcome to the Team!</h2>
            <p className="text-gray-600 mb-4">You&apos;ve successfully joined {inviteInfo?.company_name}.</p>
            <p className="text-sm text-gray-500">Redirecting to dashboard...</p>
          </CardContent>
        </Card>
      </div>
    );
  }
  
  return (
    <div className="min-h-screen flex items-center justify-center bg-gradient-to-br from-violet-50 to-indigo-100 p-4">
      <Card className="w-full max-w-md">
        <CardHeader className="text-center">
          <div className="w-16 h-16 bg-violet-100 rounded-full flex items-center justify-center mx-auto mb-4">
            <Building2 className="w-8 h-8 text-violet-600" />
          </div>
          <CardTitle>Team Invitation</CardTitle>
          <CardDescription>
            You&apos;ve been invited to join <strong className="text-violet-600">{inviteInfo?.company_name}</strong>
          </CardDescription>
        </CardHeader>
        
        <CardContent className="space-y-6">
          {error && (
            <Alert variant="destructive">
              <AlertDescription>{error}</AlertDescription>
            </Alert>
          )}
          
          {/* Invite Details */}
          <div className="bg-gray-50 rounded-lg p-4 space-y-2">
            <div className="flex items-center gap-2 text-sm">
              <Mail className="w-4 h-4 text-gray-400" />
              <span className="text-gray-600">Invited email:</span>
              <span className="font-medium">{inviteInfo?.email}</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <User className="w-4 h-4 text-gray-400" />
              <span className="text-gray-600">Invited by:</span>
              <span className="font-medium">{inviteInfo?.invited_by_name || inviteInfo?.invited_by_email}</span>
            </div>
            <div className="flex items-center gap-2 text-sm">
              <Building2 className="w-4 h-4 text-gray-400" />
              <span className="text-gray-600">Role:</span>
              <span className="font-medium capitalize">{inviteInfo?.role}</span>
            </div>
          </div>
          
          {/* Already logged in */}
          {currentUser && !showLogin && !showSignup && (
            <div className="space-y-4">
              <p className="text-sm text-gray-600 text-center">
                You&apos;re logged in as <strong>{currentUser.email}</strong>
              </p>
              
              {currentUser.email.toLowerCase() === inviteInfo?.email.toLowerCase() ? (
                <Button 
                  className="w-full" 
                  onClick={handleAcceptAsCurrentUser}
                  disabled={processing}
                >
                  {processing ? (
                    <Loader2 className="w-4 h-4 mr-2 animate-spin" />
                  ) : (
                    <CheckCircle className="w-4 h-4 mr-2" />
                  )}
                  Accept & Join Team
                </Button>
              ) : (
                <div className="space-y-2">
                  <Alert>
                    <AlertDescription>
                      This invitation is for {inviteInfo?.email}. Please log in with that account or create a new account.
                    </AlertDescription>
                  </Alert>
                  <div className="grid grid-cols-2 gap-2">
                    <Button variant="outline" onClick={() => setShowLogin(true)}>
                      <LogIn className="w-4 h-4 mr-2" />
                      Login
                    </Button>
                    <Button variant="outline" onClick={() => setShowSignup(true)}>
                      <UserPlus className="w-4 h-4 mr-2" />
                      Sign Up
                    </Button>
                  </div>
                </div>
              )}
            </div>
          )}
          
          {/* Not logged in - show options */}
          {!currentUser && !showLogin && !showSignup && (
            <div className="space-y-4">
              <p className="text-sm text-gray-600 text-center">
                Choose how to proceed:
              </p>
              <div className="grid grid-cols-2 gap-3">
                <Button variant="outline" onClick={() => setShowLogin(true)} className="h-20 flex-col">
                  <LogIn className="w-5 h-5 mb-2" />
                  <span className="text-xs">I have an account</span>
                </Button>
                <Button variant="outline" onClick={() => setShowSignup(true)} className="h-20 flex-col">
                  <UserPlus className="w-5 h-5 mb-2" />
                  <span className="text-xs">Create new account</span>
                </Button>
              </div>
            </div>
          )}
          
          {/* Login Form */}
          {showLogin && (
            <form onSubmit={handleLoginAndAccept} className="space-y-4">
              <div>
                <Label>Email</Label>
                <Input
                  type="email"
                  value={loginData.email}
                  onChange={(e) => setLoginData({...loginData, email: e.target.value})}
                  placeholder={inviteInfo?.email}
                  required
                />
              </div>
              <div>
                <Label>Password</Label>
                <Input
                  type="password"
                  value={loginData.password}
                  onChange={(e) => setLoginData({...loginData, password: e.target.value})}
                  required
                />
              </div>
              <div className="flex gap-2">
                <Button type="button" variant="outline" onClick={() => setShowLogin(false)} className="flex-1">
                  Back
                </Button>
                <Button type="submit" disabled={processing} className="flex-1">
                  {processing ? <Loader2 className="w-4 h-4 animate-spin" /> : "Login & Join"}
                </Button>
              </div>
            </form>
          )}
          
          {/* Signup Form */}
          {showSignup && (
            <form onSubmit={handleSignupAndAccept} className="space-y-4">
              <div>
                <Label>Full Name</Label>
                <Input
                  type="text"
                  value={signupData.name}
                  onChange={(e) => setSignupData({...signupData, name: e.target.value})}
                  required
                />
              </div>
              <div>
                <Label>Email</Label>
                <Input
                  type="email"
                  value={signupData.email || inviteInfo?.email}
                  onChange={(e) => setSignupData({...signupData, email: e.target.value})}
                  placeholder={inviteInfo?.email}
                />
                <p className="text-xs text-gray-500 mt-1">Leave empty to use invited email</p>
              </div>
              <div>
                <Label>Password</Label>
                <Input
                  type="password"
                  value={signupData.password}
                  onChange={(e) => setSignupData({...signupData, password: e.target.value})}
                  required
                  minLength={8}
                />
              </div>
              <div>
                <Label>Confirm Password</Label>
                <Input
                  type="password"
                  value={signupData.confirmPassword}
                  onChange={(e) => setSignupData({...signupData, confirmPassword: e.target.value})}
                  required
                />
              </div>
              <div className="flex gap-2">
                <Button type="button" variant="outline" onClick={() => setShowSignup(false)} className="flex-1">
                  Back
                </Button>
                <Button type="submit" disabled={processing} className="flex-1">
                  {processing ? <Loader2 className="w-4 h-4 animate-spin" /> : "Create & Join"}
                </Button>
              </div>
            </form>
          )}
        </CardContent>
      </Card>
    </div>
  );
}
